import { Localized } from '../../internal/Localized';
import Styles from './Dashboard.module.css';
import { Button, Label, Textbox } from '@app/engine';
import { Telemetry } from '@shared/frontend';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/** File-local constants for dashboard UI helpers. */
const localConstants = {
  wheelEventName: 'wheel',
  // The surface tag reported by the shared dashboard ErrorView; the emitted event also carries the redacted route, which pins the exact failing screen.
  errorSurface: 'dashboard_data',
  // Collapse/expand toggle glyphs for the info panel: a question mark when collapsed, an up arrow when expanded.
  infoCollapsedGlyph: '?',
  infoExpandedGlyph: '\u2191',
  // En dash between the from-to range and the total in the pagination readout ("1-10 / 42").
  rangeSeparator: '\u2013'
} as const;

/** Selectable page sizes for the shared list controls; the first is the default. */
export const ListPageSizes = [10, 20, 40] as const;

/** Default rows-per-page for the shared list controls. */
export const DefaultListPageSize = ListPageSizes[0];

/**
 * Single source of truth for the closed set of server status values rendered as badges in dashboard data tables. The server value is effectively a key: each maps to a colour class (how the badge
 * looks) and a Localized label key (its translated display text). Aliases (e.g. canceled/cancelled) share one label key. Statuses absent from this catalogue fall back to the neutral badge and
 * their raw value until added here.
 */
const statusCatalog: Record<string, { className: string; labelKey: string }> = {
  active: { className: Styles.badgeSuccess, labelKey: 'SharedActive' },
  completed: { className: Styles.badgeSuccess, labelKey: 'StatusCompleted' },
  sent: { className: Styles.badgeSuccess, labelKey: 'StatusSent' },
  paid: { className: Styles.badgeSuccess, labelKey: 'SharedPaid' },
  connected: { className: Styles.badgeSuccess, labelKey: 'SharedConnected' },
  processed: { className: Styles.badgeSuccess, labelKey: 'StatusProcessed' },
  resolved: { className: Styles.badgeSuccess, labelKey: 'SharedResolved' },
  success: { className: Styles.badgeSuccess, labelKey: 'StatusSuccess' },
  succeeded: { className: Styles.badgeSuccess, labelKey: 'StatusSucceeded' },
  pending: { className: Styles.badgePending, labelKey: 'SharedPending' },
  queued: { className: Styles.badgePending, labelKey: 'StatusQueued' },
  in_progress: { className: Styles.badgePending, labelKey: 'SharedInProgress' },
  processing: { className: Styles.badgePending, labelKey: 'StatusProcessing' },
  trialing: { className: Styles.badgePending, labelKey: 'StatusTrialing' },
  grace: { className: Styles.badgePending, labelKey: 'StatusGrace' },
  failed: { className: Styles.badgeDanger, labelKey: 'StatusFailed' },
  error: { className: Styles.badgeDanger, labelKey: 'StatusError' },
  canceled: { className: Styles.badgeDanger, labelKey: 'StatusCanceled' },
  cancelled: { className: Styles.badgeDanger, labelKey: 'StatusCanceled' },
  past_due: { className: Styles.badgeDanger, labelKey: 'StatusPastDue' },
  blocked: { className: Styles.badgeDanger, labelKey: 'StatusBlocked' },
  inactive: { className: Styles.badgeDanger, labelKey: 'SharedInactive' },
  unpaid: { className: Styles.badgeDanger, labelKey: 'StatusUnpaid' },
  warning: { className: Styles.badgeWarning, labelKey: 'StatusWarning' },
  attention: { className: Styles.badgeWarning, labelKey: 'StatusAttention' },
  needs_review: { className: Styles.badgeWarning, labelKey: 'StatusNeedsReview' },
  incomplete: { className: Styles.badgeWarning, labelKey: 'StatusIncomplete' }
};

/**
 * Maps a status-like string to a badge variant class.
 *
 * @param status - The status string to classify.
 * @returns The CSS class for the matching badge variant.
 */
export function StatusBadgeClass(status: string | undefined | null): string {
  return statusCatalog[(status ?? '').toLowerCase()]?.className ?? Styles.badge;
}

/**
 * Resolves a server status value to its translated display label. The server value is treated as a key into the status catalogue; unknown or empty values render their raw text (or an em dash) so
 * new states stay legible until catalogued.
 *
 * @param status - The raw status string from the server.
 * @returns The Localized label for known statuses; the raw value (or an em dash) otherwise.
 */
export function StatusLabel(status: string | undefined | null): string {
  const meta = statusCatalog[(status ?? '').toLowerCase()];
  if (meta) return Localized[meta.labelKey] ?? status ?? '—';
  return status ?? '—';
}

/**
 * A status badge.
 *
 * @param props - Component props.
 * @param props.status - The status string to render.
 * @returns The badge element.
 */
export function StatusBadge({ status }: { status: string | undefined | null }) {
  return <Label classNameContainer={`${Styles.badge} ${StatusBadgeClass(status)}`} text={StatusLabel(status)} />;
}

/**
 * A centered loading spinner view.
 *
 * @param props - Component props.
 * @param props.className - Optional container class overriding the default full-height centered box (e.g. the bounded onboarding panel layout). Defaults to the full-height centered layout used
 *   by the full-screen shell and dashboard data views.
 * @returns The loading view element.
 */
export function LoadingView({ className }: { className?: string } = {}) {
  return (
    <div className={className ?? Styles.centered}>
      <div className={Styles.spinner} />
    </div>
  );
}

/**
 * The single loading layout for the onboarding work panel: the standard onboarding card chrome wrapping a height-bounded centered spinner and an optional status note. Used for every onboarding
 * loading state — wizard step resolution, the account step fetching its catalog, and the billing/phone steps running their server operations — so the work panel keeps a stable, card-sized
 * footprint across loading and loaded states instead of ballooning to the full-height spinner and then collapsing (the prior flicker).
 *
 * @param props - Component props.
 * @param props.title - Optional card title, kept identical to the loaded step so its header does not pop in on resolution.
 * @param props.note - Optional centered status note shown beneath the spinner (e.g. "Provisioning your number…").
 * @returns The onboarding loading panel element.
 */
export function OnboardingLoading({ title, note }: { title?: string; note?: string }) {
  return (
    <Card title={title} className={Styles.onboardingCard}>
      <LoadingView className={Styles.onboardingLoading} />
      {note ? <Label classNameContainer={Styles.gateNote} text={note} /> : null}
    </Card>
  );
}

/**
 * A centered error view with a retry action.
 *
 * @param props - Component props.
 * @param props.onRetry - Optional retry callback.
 * @returns The error view element.
 */
export function ErrorView({ onRetry }: { onRetry?: () => void }) {
  // Analytics signal that a data surface failed for a real visitor; the semantic layer attaches the redacted route so no call-site plumbing is needed.
  useEffect(() => {
    Telemetry.SurfaceError(localConstants.errorSurface);
  }, []);

  return (
    <div className={Styles.centered}>
      <Label classNameContainer={Styles.errorText} text={Localized.DashError} />
      {onRetry ? <Button text={Localized.DashRetry} classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary}`} onClickHandler={onRetry} /> : null}
    </div>
  );
}

/**
 * An empty-state view.
 *
 * @param props - Component props.
 * @param props.message - Message to display.
 * @returns The empty view element.
 */
export function EmptyView({ message }: { message: string }) {
  return <Label classNameContainer={Styles.empty} text={message} />;
}

/**
 * Horizontal-scroll wrapper for dashboard data tables. Vertical wheel input over the wrapper pans the table sideways until it reaches an edge, after which the page resumes scrolling (mirrors the
 * portal DataTable); the scrollbar chrome stays hidden via .tableWrap, so wheel and drag/touch are the only scroll affordances.
 *
 * @param props - Component props.
 * @param props.children - The table element.
 * @returns The wrapper element.
 */
export function TableWrap({ children }: { children: ReactNode }) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleWheel = useCallback((event: WheelEvent) => {
    const container = event.currentTarget;
    if (!(container instanceof HTMLDivElement)) return;

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    if (maxScrollLeft <= 0 || event.deltaY === 0) return;

    // At the edge in the wheel's direction, hand the event back to the page so vertical scrolling resumes.
    const atLeftEdge = container.scrollLeft <= 0;
    const atRightEdge = container.scrollLeft >= maxScrollLeft;
    if ((event.deltaY < 0 && atLeftEdge) || (event.deltaY > 0 && atRightEdge)) return;

    event.preventDefault();
    container.scrollLeft = Math.min(Math.max(container.scrollLeft + event.deltaY, 0), maxScrollLeft);
  }, []);

  // React's synthetic onWheel registers passively (preventDefault is ignored), so the listener is attached natively as non-passive via a callback ref.
  const handleContainerRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (containerRef.current) containerRef.current.removeEventListener(localConstants.wheelEventName, handleWheel);
      containerRef.current = element;
      if (element) element.addEventListener(localConstants.wheelEventName, handleWheel, { passive: false });
    },
    [handleWheel]
  );

  useEffect(() => {
    return () => {
      containerRef.current?.removeEventListener(localConstants.wheelEventName, handleWheel);
    };
  }, [handleWheel]);

  return (
    <div ref={handleContainerRef} className={Styles.tableWrap}>
      {children}
    </div>
  );
}

/**
 * A titled card panel.
 *
 * @param props - Component props.
 * @param props.title - Optional section title.
 * @param props.titleHint - Optional tooltip text for the title; falls back to the title when omitted.
 * @param props.children - Card content.
 * @param props.actions - Optional header actions.
 * @param props.feedback - Optional transient feedback element rendered as a full-width strip beneath the header (see FeedbackMessage).
 * @param props.className - Optional extra class applied to the card root.
 * @returns The card element.
 */
export function Card({
  title,
  titleHint,
  children,
  actions,
  feedback,
  className
}: {
  title?: string;
  titleHint?: string;
  children: ReactNode;
  actions?: ReactNode;
  feedback?: ReactNode;
  className?: string;
}) {
  return (
    <div className={className ? `${Styles.card} ${className}` : Styles.card}>
      {title || actions ? (
        <div className={Styles.cardHeader}>
          {title ? (
            <div className={Styles.cardHeaderLead}>
              <Label classNameContainer={`${Styles.sectionTitle} ${Styles.cardHeaderTitle}`} text={title} title={titleHint ?? title} />
            </div>
          ) : (
            // Raw <span>: empty spacer preserving the header's space-between layout (actions stay right-aligned) when there is no title; engine Label requires text.
            <span />
          )}
          {actions}
        </div>
      ) : null}
      {feedback}
      {children}
    </div>
  );
}

/** A single field rendered in the dashboard data layout: a label with its value below. */
export interface IDataRow {
  label: string;
  value: ReactNode;
  hint?: string;
}

/**
 * The standard dashboard data layout: a stack of two-row bubbles, each showing a field label above its value. Replaces the older key/value grid so every panel that lists field/value pairs (Call
 * detail, Notifications, Account, Billing) reads identically across the dashboard.
 *
 * @param props - Component props.
 * @param props.rows - The field rows to render; falsy rows are skipped so callers can drop a field inline.
 * @returns The data list element.
 */
export function DataList({ rows }: { rows: ReadonlyArray<IDataRow | null | false> }) {
  return (
    <div className={Styles.dataList}>
      {rows
        .filter((row): row is IDataRow => Boolean(row))
        .map((row) => (
          <div key={row.label} className={Styles.dataBubble}>
            <Label classNameContainer={Styles.dataKey} text={row.label} title={row.hint ?? row.label} />
            <div className={Styles.dataValue}>{typeof row.value === 'string' ? <Label text={row.value} /> : row.value}</div>
          </div>
        ))}
    </div>
  );
}

/**
 * The standard dashboard info layout: a collapsible help panel. The body starts collapsed, showing only the header and a round toggle on the right ("?" collapsed, up arrow expanded); clicking it
 * smoothly expands or collapses the prose. Used by every "How … works" help panel so help reads identically across the dashboard.
 *
 * @param props - Component props.
 * @param props.title - The panel header.
 * @param props.children - The help prose.
 * @returns The info panel element.
 */
export function InfoPanel({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((prev) => !prev);
  return (
    <div className={Styles.card}>
      {/* The whole header strip is the toggle target (matching the round button), so clicking the title expands/collapses the panel too. The button carries no own handler: its native click
          bubbles to this row, keeping a single toggle per click and preserving keyboard activation. */}
      <div className={`${Styles.infoHeader} ${Styles.infoHeaderClickable}`} onClick={toggle}>
        <Label classNameContainer={Styles.infoTitle} text={title} />
        <Button
          text={open ? localConstants.infoExpandedGlyph : localConstants.infoCollapsedGlyph}
          title={open ? Localized.SharedCollapse : Localized.SharedExpand}
          classNameButtonEnabled={Styles.infoToggle}
        />
      </div>
      <div className={`${Styles.infoBody} ${open ? '' : Styles.infoBodyCollapsed}`}>{children}</div>
    </div>
  );
}

/**
 * The shared search + pagination panel that sits as the first panel under a list page header. It renders a free-text search box, a rows-per-page selector (10/20/40), a from-to/total readout
 * ("1-10 / 42"), and previous/next controls. Paging is server-side: the page owns the `offset`/`limit`/search state and re-fetches on change; this component only renders the controls and reports
 * changes. `pageCount` is the number of rows currently displayed so the range end is correct on a short final page.
 *
 * @param props - Component props.
 * @param props.searchValue - The current search text.
 * @param props.onSearchChange - Called when the search text changes.
 * @param props.searchPlaceholder - Placeholder describing the searchable fields.
 * @param props.offset - The current row offset (0-based).
 * @param props.limit - The current page size.
 * @param props.total - The total number of matching rows.
 * @param props.pageCount - The number of rows on the current page.
 * @param props.onOffsetChange - Called with the new offset when paging.
 * @param props.onLimitChange - Called with the new page size.
 * @param props.loading - Whether a fetch is in flight (disables paging).
 * @returns The list controls panel.
 */
export function ListControls(props: {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  offset: number;
  limit: number;
  total: number;
  pageCount: number;
  onOffsetChange: (offset: number) => void;
  onLimitChange: (limit: number) => void;
  loading?: boolean;
}) {
  const { searchValue, onSearchChange, searchPlaceholder, offset, limit, total, pageCount, onOffsetChange, onLimitChange, loading } = props;
  const rangeStart = total > 0 && pageCount > 0 ? offset + 1 : 0;
  const rangeEnd = offset + pageCount;
  const hasPrev = offset > 0;
  const hasNext = offset + limit < total;
  return (
    <div className={`${Styles.card} ${Styles.listControls}`}>
      <Textbox value={searchValue} onChange={onSearchChange} placeholder={searchPlaceholder} classNameContainer={Styles.listSearch} classNameText={Styles.input} />
      <div className={Styles.listPager}>
        <div className={Styles.listPagerGroup}>
          <label className={`${Styles.listPerPage} ${Styles.listPill}`}>
            <Label classNameContainer={Styles.listDim} text={Localized.ListPerPage} />
            <select className={Styles.input} value={limit} onChange={(event) => onLimitChange(Number(event.target.value))}>
              {ListPageSizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <Label classNameContainer={`${Styles.listDim} ${Styles.listPill}`} text={`${rangeStart}${localConstants.rangeSeparator}${rangeEnd} / ${total}`} />
        </div>
        <div className={Styles.listPagerGroup}>
          <Button
            text={Localized.ListPrevious}
            disabled={!hasPrev || loading}
            onClickHandler={() => onOffsetChange(Math.max(0, offset - limit))}
            classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
            classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall}`}
          />
          <Button
            text={Localized.ListNext}
            disabled={!hasNext || loading}
            onClickHandler={() => onOffsetChange(offset + limit)}
            classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
            classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall}`}
          />
        </div>
      </div>
    </div>
  );
}

export { Styles as DashStyles };
